import * as system from 'durandal/system';
import * as ko from 'knockout';

import { FileStorageDownloadLinkViewModel, fileStorageService } from '@/features/file-attachments';
import { SessionTimeoutService } from '@/legacy/services/sessionTimeout';
import * as userService from '@/legacy/services/user';
import * as plootoUtils from '@/legacy/utils/plooto';
import * as addModels from '@/legacy/viewmodels/addModels';
import * as viewModels from '@/legacy/viewmodels/viewModels';

const API_PREFIX = 'v1';

interface PopupParameters {
  location: number;
  width: number;
  height: number;
  left?: number;
  top?: number;
}

export class PaymentRequestEmailViewModel {
  public id: string;
}
export class PaymentRequestContactEmailViewModel {
  public id: string;

  public memo: string;

  public emailKey: string;

  public emailVerificationKey: string;

  public status: string;

  public date: string;

  public amount: number;

  public currency: string;

  public debitedDate: string;

  public debitTransactionError: string;
}

export class PaymentRequestPinBindingModel {
  public code: string;

  public emailVerificationCode: string;

  public paymentRequestCurrentStateHash: string;

  public payeePaymentMethodId: string;
}
export class PaymentRequestPrefilledBindingModel {
  public emailVerificationCode: string;

  public payeeAccountId: string;
}

export enum PaymentRequestDirectDebitStatus {
  BankDetailsPending = 0,
  BankDetailsAdded,
  Accepted,
}
export class PayeePaymentMethodViewModel {
  public fundAccountId: string;

  public discriminator: string;
}
export class PayeePaymentMethodDirectDebitViewModel extends PayeePaymentMethodViewModel {
  public payeePaymentMethodId: string;

  public directDebitStatus: PaymentRequestDirectDebitStatus;

  public microDeposit: viewModels.MicroDepositViewModel;

  public locked: boolean;

  public bank: string;

  public maskedAccountNumber: string;
}
export class PayeePaymentMethodPayeeAccountViewModel extends PayeePaymentMethodViewModel {
  public payeeAccountId: string;

  public bank: string;

  public country: string;

  public maskedAccountNumber: string;

  public verificationIdentifier: string;
}

export class PayeePaymentMethodCreditCardViewModel extends PayeePaymentMethodViewModel {
  public payeePaymentMethodId: string;

  public directDebitStatus: PaymentRequestDirectDebitStatus;

  public locked: boolean;

  public creditCardId: number;

  public creditCardType: string;

  public maskedCreditCardNumber: string;

  public expirationMonth: number;

  public expirationYear: number;
}

export interface IPaymentRequestPayeeAccountDetailsViewModel {
  accountNumber: string;

  institution: string;
  transit: string;

  routingNumber: string;

  confirmed: boolean;
}
export class ReuseBankingInformationBindingModel {
  public paymentRequestCurrentStateHash: string;

  public emailVerificationCode: string;

  public payeePaymentMethodId: string;
}
export class PaymentRequestRejectionBindingModel {
  public rejectionOption: string;

  public memo: string;
}

export class VerifiedBankValidateBindingModel {
  public accountDetails: addModels.BankAccountDetailsAddModel;

  public emailVerificationCode: string;

  public paymentRequestCurrentStateHash: string;
}

export class PaymentRequestContactPaymentRequestsBindingModel {
  public emailVerificationCode: string;

  public fromDate: string;

  public toDate: string;

  public completedOnly: boolean;
}
export class UniversalPayeePublicInfo {
  public id: KnockoutObservable<string> = ko.observable('');

  public name: KnockoutObservable<string> = ko.observable('');

  public phone: KnockoutObservable<string> = ko.observable('');

  public address: KnockoutObservable<string> = ko.observable('');

  public unit: KnockoutObservable<string> = ko.observable('');

  public city: KnockoutObservable<string> = ko.observable('');

  public zipCode: KnockoutObservable<string> = ko.observable('');

  public country: KnockoutObservable<string> = ko.observable('');

  public state: KnockoutObservable<string> = ko.observable('');

  public status: KnockoutObservable<string> = ko.observable('');

  public Update(info) {
    for (const property in info) {
      // if updated data doesnt specify property skip
      if (info[property] !== undefined) {
        const newValue = ko.utils.unwrapObservable(info[property]);

        if (!ko.isComputed(this[property]) && ko.isObservable(this[property])) {
          this[property](newValue);
        }
      }
    }
  }
}
export class KoEmailPaymentRequest {
  public id: KnockoutObservable<string> = ko.observable('');

  public emailKey: KnockoutObservable<string> = ko.observable('');

  public emailVerificationCode: KnockoutObservable<string> = ko.observable('');

  public emailVerificationCodeValid: KnockoutObservable<boolean> = ko.observable(false);

  public amount: KnockoutObservable<number> = ko.observable(0);

  public currency: KnockoutObservable<string> = ko.observable('');

  public country: KnockoutObservable<string> = ko.observable('');

  public memo: KnockoutObservable<string> = ko.observable('');

  public deleted: KnockoutObservable<boolean> = ko.observable(false);

  public padOnly: KnockoutObservable<boolean> = ko.observable(false);

  public payeeLocale: KnockoutObservable<string> = ko.observable('');

  public paymentRequestPaymentMethodSource: KnockoutObservable<string> = ko.observable('');

  public isPreview: KnockoutObservable<boolean> = ko.observable(false);

  public paymentDebitTransactionStatus = ko.observable<string>(null);

  public paymentDebitTransactionError = ko.observable<string>(null);

  public payeeName = ko.observable<string>(null);

  public createdDate: KnockoutObservable<Date> = ko.observable(new Date());

  public universalPayee: KnockoutObservable<UniversalPayeePublicInfo> = ko.observable(
    new UniversalPayeePublicInfo()
  );

  public payeePaymentMethodId: KnockoutObservable<string> = ko.observable('');

  public payeePaymentMethods = ko.observableArray<PayeePaymentMethodViewModel>();

  public creditCardPayeePaymentMethods = ko.observableArray<PayeePaymentMethodViewModel>();

  public date: KnockoutObservable<string> = ko.observable('');

  public estimatedDebitDate: KnockoutObservable<string> = ko.observable('');

  public creditCardAvailable: KnockoutObservable<boolean> = ko.observable(false);

  public recurringPlan: KnockoutObservable<boolean> = ko.observable(false);

  public intervalNumber: KnockoutObservable<number> = ko.observable(1);

  public interval: KnockoutObservable<string> = ko.observable('Month');

  public duration: KnockoutObservable<number> = ko.observable(0);

  public startDay: KnockoutObservable<number> = ko.observable(0);

  public nextRecurringPaymentDay: KnockoutObservable<Date> = ko.observable(new Date());

  public processingStatus = ko.observable<string>();

  public paymentRequestCurrentStateHash = ko.observable<string>();

  public payeeEmail = ko.observable<string>();

  public hasAttachments = ko.observable<boolean>(false);

  public attachmentStatus = ko.observable<string>('loadRequired');

  public attachments = ko.observableArray<any>([]);

  public isSecondaryBankLoginEnabled = ko.observable<boolean>();

  constructor(info: any) {
    this.Update(info);
  }

  public Update(paymentRequestInfo) {
    for (const property in paymentRequestInfo) {
      // if updated data doesnt specify property skip
      if (paymentRequestInfo[property] !== undefined) {
        const newValue = ko.utils.unwrapObservable(paymentRequestInfo[property]);

        if (property == 'universalPayee') {
          this.universalPayee().Update(newValue);
        } else if (property == 'payeePaymentMethods') {
          this.payeePaymentMethods(newValue as Array<PayeePaymentMethodViewModel>);
        } else if (property == 'creditCardPayeePaymentMethods') {
          this.creditCardPayeePaymentMethods(newValue as Array<PayeePaymentMethodViewModel>);
        } else if (!ko.isComputed(this[property]) && ko.isObservable(this[property])) {
          this[property](newValue);
        }
      }
    }
  }

  public getTransactionErrorDescription(isDebit: boolean) {
    switch (this.paymentDebitTransactionError()) {
      case 'Unknown':
        return `The ${isDebit ? 'debit' : 'credit'} was rejected for unknown reason`;
      case 'AccountClosed':
        return `The ${isDebit ? 'source' : 'destination'} bank account is closed.`;
      case 'AccountFrozen':
        return `The ${isDebit ? 'source' : 'destination'} bank account is frozen.`;
      case 'AccountNotFound':
        return `The ${isDebit ? 'source' : 'destination'} bank account number was not found.`;
      case 'CurrencyMismatch':
        return `The ${
          isDebit ? 'source' : 'destination'
        } bank account currency does not match the payment currency.`;
      case 'AccountHolderDeceased':
        return `The ${isDebit ? 'source' : 'destination'} bank account holder is deceased.`;
      case 'FundsNotCleared':
        return `The ${isDebit ? 'debit' : 'credit'} transaction failed because the ${
          isDebit ? 'source' : 'destination'
        } funds have not fully cleared.`;
      case 'IncorrectAccountHolderName':
        return `The ${
          isDebit ? 'source' : 'destination'
        } bank account holder name does not match the contact name.`;
      case 'NotAllowed':
        return `The ${isDebit ? 'source' : 'destination'} bank rejected this transaction.`;
      case 'PaymentStopped':
        return `The ${isDebit ? 'source' : 'destination'} bank stopped this transaction.`;
      case 'Nsf':
        return `The ${
          isDebit ? 'source' : 'destination'
        } bank account did not have enough funds for the transaction.`;
      case 'StateRegionNotSupported':
        return `The ${isDebit ? 'source' : 'destination'} bank is not in a supported Region/State.`;

      case 'PaymentFailure':
        return 'Payment failed. Please reach out to Plooto support.';

      default:
        return `The ${isDebit ? 'debit' : 'credit'} was rejected for unknown reason`;
    }
  }

  public getTransactionErrorTitle(isDebit: boolean) {
    switch (this.paymentDebitTransactionError()) {
      case 'Unknown':
        return 'Unknown Reason';
      case 'AccountClosed':
        return 'Account Closed';
      case 'AccountFrozen':
        return 'Account Frozen';
      case 'AccountNotFound':
        return 'Account Not Found';
      case 'CurrencyMismatch':
        return 'Currency Mismatch';
      case 'AccountHolderDeceased':
        return 'Account Holder Deceased';
      case 'FundsNotCleared':
        return 'Non-Sufficient Funds due to funds not cleared';
      case 'IncorrectAccountHolderName':
        return 'Incorrect Account Holder Name';
      case 'NotAllowed':
        return 'Not Allowed';
      case 'PaymentStopped':
        return 'Payment was stopped by bank account owner';
      case 'Nsf':
        return 'Non-Sufficient Funds';

      case 'PaymentFailure':
        return 'Payment failed. Please reach out to Plooto support.';

      case 'StateRegionNotSupported':
        return 'State/Region Not Supported';
      default:
        return 'Unknown Reason';
    }
  }
}

export class EmailPaymentRequestService {
  public GetPaymentRequestByEmailKey(
    emailKey: string,
    emailVerificationCode: string = null
  ): JQueryPromise<KoEmailPaymentRequest> {
    return system
      .defer<KoEmailPaymentRequest>((dfd) => {
        userService.Instance.AuthorizedGetFromUri(
          `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/emailPaymentRequests/${emailKey}`,
          { emailVerificationCode }
        )
          .done((tokenData) => {
            if (plootoUtils.IsNullOrUndefined(tokenData) || tokenData.error === true) {
              dfd.reject(tokenData);
              return;
            }

            dfd.resolve(new KoEmailPaymentRequest(tokenData));
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public getContactPaymentRequests(
    emailKey: string,
    model: PaymentRequestContactPaymentRequestsBindingModel
  ): JQueryPromise<Array<PaymentRequestContactEmailViewModel>> {
    return system
      .defer<Array<PaymentRequestContactEmailViewModel>>((dfd) => {
        userService.Instance.AuthorizedGetFromUri(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/contactPaymentRequests`,
          model
        )
          .done((data) => {
            if (plootoUtils.IsNullOrUndefined(data) || data.error === true) {
              dfd.reject(data);
              return;
            }

            dfd.resolve(data);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public countContactPaymentRequests(emailKey: string): JQueryPromise<number> {
    return system
      .defer<number>((dfd) => {
        userService.Instance.AuthorizedGet(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/countUnpaidPaymentRequests`
        )
          .done((data) => {
            if (plootoUtils.IsNullOrUndefined(data) || data.error === true) {
              dfd.reject(data);
              return;
            }

            dfd.resolve(data);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public RemovePayeeAccount(
    emailKey: string,
    emailVerificationCode: string,
    creditCardId: number
  ): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        userService.Instance.AuthorizedDelete(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/${emailVerificationCode}/${creditCardId}`
        )
          .then((result) => {
            if (plootoUtils.IsNullOrUndefined(result) || result.error === true) {
              dfd.reject(result);
              return;
            }
            dfd.resolve(result);
          })
          .fail((e) => {
            console.log('error', e);
            dfd.reject(e);
          });
      })
      .promise();
  }

  public PostAddAccountDetails(
    emailKey: string,
    accountDetails: addModels.PaymentRequestFinancialDetailsBindingModel
  ): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/accountDetails`,
          accountDetails
        )
          .then((emailStatus) => {
            if (plootoUtils.IsNullOrUndefined(emailStatus) || emailStatus.error === true) {
              dfd.reject(emailStatus);
              return;
            }
            dfd.resolve(emailStatus);
          })
          .fail((e) => {
            console.log('error', e);
            dfd.reject(e);
          });
      })
      .promise();
  }

  public PostPaymentRequestPrefilled(
    emailKey: string,
    paymentRequestPrefilledBindingModel: PaymentRequestPrefilledBindingModel
  ): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/prefilledAccountDetails`,
          paymentRequestPrefilledBindingModel
        )
          .then((emailStatus) => {
            if (plootoUtils.IsNullOrUndefined(emailStatus) || emailStatus.error === true) {
              dfd.reject(emailStatus);
              return;
            }
            dfd.resolve(emailStatus);
          })
          .fail((e) => {
            console.log('error', e);
            dfd.reject(e);
          });
      })
      .promise();
  }

  public PostVerifiedBankValidate(
    emailKey: string,
    accountDetails: addModels.VerifiedBankValidateBindingModel
  ): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/verifiedBankValidate`,
          accountDetails
        )
          .then((emailStatus) => {
            if (plootoUtils.IsNullOrUndefined(emailStatus) || emailStatus.error === true) {
              dfd.reject(emailStatus);
              return;
            }
            dfd.resolve(emailStatus);
          })
          .fail((e) => {
            console.log('error', e);
            dfd.reject(e);
          });
      })
      .promise();
  }

  public PostBankPinCode(
    emailKey: string,
    bankAccountValidateBindingModel: PaymentRequestPinBindingModel
  ): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/pinCode`,
          bankAccountValidateBindingModel
        )
          .then((emailStatus) => {
            if (plootoUtils.IsNullOrUndefined(emailStatus) || emailStatus.error === true) {
              dfd.reject(emailStatus);
              return;
            }
            dfd.resolve(emailStatus);
          })
          .fail((e) => {
            console.log('error', e);
            dfd.reject(e);
          });
      })
      .promise();
  }

  public PostReuseBankingInformation(
    emailKey: string,
    reuseBankingInformationBindingModel: ReuseBankingInformationBindingModel
  ): JQueryPromise<any> {
    return system
      .defer((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/reuseBankingDetails`,
          reuseBankingInformationBindingModel
        )
          .then((emailStatus) => {
            if (plootoUtils.IsNullOrUndefined(emailStatus) || emailStatus.error === true) {
              dfd.reject(emailStatus);
              return;
            }
            dfd.resolve(emailStatus);
          })
          .fail((e) => {
            console.log('error', e);
            dfd.reject(e);
          });
      })
      .promise();
  }

  public PostRejectPaymentRequest(
    emailKey: string,
    reuseBankingInformationBindingModel: PaymentRequestRejectionBindingModel
  ): JQueryPromise<PaymentRequestEmailViewModel> {
    return system
      .defer<PaymentRequestEmailViewModel>((dfd) => {
        userService.Instance.AuthorizedPost(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/rejectPaymentRequest`,
          reuseBankingInformationBindingModel
        )
          .then((emailStatus) => {
            if (plootoUtils.IsNullOrUndefined(emailStatus) || emailStatus.error === true) {
              dfd.reject(emailStatus);
              return;
            }
            dfd.resolve(emailStatus);
          })
          .fail((e) => {
            console.log('error', e);
            dfd.reject(e);
          });
      })
      .promise();
  }

  public GetInvoiceAttachments(emailKey: string): JQueryPromise<Array<any>> {
    return system
      .defer<Array<any>>((dfd) => {
        userService.Instance.AuthorizedGet(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/emailPaymentRequests/${emailKey}/attachments`
        )
          .done((payments) => {
            if (plootoUtils.IsNullOrUndefined(payments) || payments.error === true) {
              dfd.reject(payments);
              return;
            }
            dfd.resolve(payments);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public GetInvoiceAttachment(invoiceAttachmentDetails) {
    return system
      .defer((dfd) => {
        // default parameter values
        const popupHeight = 1068;
        const popupWidth = 800;
        const popupCentered = true;
        // default parameters
        const parameters: PopupParameters = {
          location: 1,
          width: popupWidth,
          height: popupHeight,
        };
        // center pop-up if needed
        if (popupCentered) {
          parameters.left = (window.screen.width - popupWidth) / 2;
          parameters.top = (window.screen.height - popupHeight) / 2;
        }
        // flatten all parameters into a string
        const features = $.map(parameters, (value, name) => `${name}=${value}`).join(',');

        // open pop-up without URL first to ensure that pop-up blocker wouldn't prevent it after our delay
        const popup = window.open('', '', features);
        if (popup.document) {
          popup.document.write(
            '<html><head><title>Connecting...</title></head><body></body></html>'
          );
        }

        popup.location.href = invoiceAttachmentDetails.url;
      })
      .promise();
  }

  public async GetInvoiceFileStorageAttachment(emailKey: string, companyFileStorageFileId: string) {
    const fileStorageDownloadLinkViewModel = await this.generateDownloadFileAttachmentLink(
      emailKey,
      companyFileStorageFileId
    );
    await fileStorageService.viewFileStorageFile(fileStorageDownloadLinkViewModel);
  }

  private generateDownloadFileAttachmentLink(emailKey: string, companyFileStorageFileId: string) {
    return system
      .defer<FileStorageDownloadLinkViewModel>((dfd) => {
        userService.Instance.AuthorizedGet(
          `${
            import.meta.env.APP_URLS_API_URL
          }/v1/emailPaymentRequests/${emailKey}/fileStorageAttachments/${companyFileStorageFileId}`
        )
          .then((data) => {
            if (plootoUtils.IsNullOrUndefined(data) || data.error === true) {
              if (data.type === 'invalid.authentication') {
                SessionTimeoutService.showTimeout();
              }
              dfd.reject(data);
              return;
            }
            dfd.resolve(data);
          })
          .fail((error) => console.error(error));
      })
      .promise();
  }
}
export const Instance = new EmailPaymentRequestService();
